import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, Download, Search, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STATUS_META = {
  new: { label: "New", cls: "text-blue-400 border-blue-500/50 bg-blue-500/10" },
  under_review: { label: "Under Review", cls: "text-amber-400 border-amber-500/50 bg-amber-500/10" },
  deployed: { label: "Deployed", cls: "text-emerald-400 border-emerald-500/50 bg-emerald-500/10" },
  closed: { label: "Closed", cls: "text-gray-400 border-white/15 bg-white/5" },
};

const FILTERS = ["all", "new", "under_review", "deployed", "closed"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login"));
  }, [navigate]);

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries", { params: { status: filter } });
      setEnquiries(data);
    } catch {
      toast.error("Failed to load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (user) fetchEnquiries();
  }, [user, fetchEnquiries]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enquiries;
    return enquiries.filter((e) =>
      [e.name, e.email, e.company, e.facility_type].some((f) => (f || "").toLowerCase().includes(q))
    );
  }, [enquiries, search]);

  const stats = useMemo(
    () => ({
      total: enquiries.length,
      new: enquiries.filter((e) => e.status === "new").length,
      deployed: enquiries.filter((e) => e.status === "deployed").length,
      review: enquiries.filter((e) => e.status === "under_review").length,
    }),
    [enquiries]
  );

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Status updated to ${STATUS_META[status].label}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/enquiries/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "apex_k9_enquiries.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/admin/login");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#08090B] flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500">Verifying clearance...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#08090B] text-gray-100">
      <header className="border-b border-white/10 bg-[#0F1115]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/images/logo.png" alt="Frontline K9 Security" className="h-10 w-auto" />
            <div>
              <p className="font-display font-extrabold uppercase tracking-tight leading-none">
                Command <span className="text-blue-500">Center</span>
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="export-csv-button"
              onClick={exportCsv}
              className="clip-corners border border-white/15 hover:border-blue-500/50 text-gray-300 hover:text-blue-400 font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 flex items-center gap-2 transition-colors duration-300"
            >
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="clip-corners bg-blue-600 hover:bg-blue-500 text-white font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 flex items-center gap-2 transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            ["Total Enquiries", stats.total, "stat-total"],
            ["Awaiting Review", stats.new, "stat-new"],
            ["Under Review", stats.review, "stat-review"],
            ["Deployed", stats.deployed, "stat-deployed"],
          ].map(([label, value, id]) => (
            <div key={id} data-testid={id} className="clip-corners bg-[#0F1115] border border-white/10 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">{label}</p>
              <p className="font-display font-black text-4xl mt-2">{value}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-tab-${f}`}
                onClick={() => setFilter(f)}
                className={`clip-corners px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                  filter === f ? "bg-blue-600 border-blue-500 text-white" : "border-white/10 text-gray-400 hover:border-blue-500/40 hover:text-gray-200"
                }`}
              >
                {f === "all" ? "All" : STATUS_META[f].label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="enquiry-search-input"
                placeholder="Search name, email, company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-[#0F1115] border border-white/10 focus:border-blue-500/60 outline-none pl-9 pr-4 py-2.5 text-sm text-gray-100 placeholder:text-gray-600 w-64 transition-colors duration-300"
              />
            </div>
            <button
              data-testid="refresh-enquiries-button"
              onClick={fetchEnquiries}
              className="clip-corners border border-white/15 p-2.5 text-gray-400 hover:text-blue-400 hover:border-blue-500/50 transition-colors duration-300"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {visible.length === 0 ? (
          <div data-testid="enquiries-empty-state" className="clip-corners bg-[#0F1115] border border-white/10 p-16 text-center">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500">No enquiries in this channel</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {visible.map((e) => (
              <article
                key={e.id}
                data-testid={`enquiry-row-${e.id}`}
                className="clip-corners bg-[#0F1115] border border-white/10 hover:border-blue-500/30 p-6 transition-colors duration-300"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-1">
                      <h3 className="font-display font-bold text-lg text-gray-100">{e.name}</h3>
                      <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2.5 py-1 ${STATUS_META[e.status]?.cls || STATUS_META.new.cls}`}>
                        {STATUS_META[e.status]?.label || e.status}
                      </span>
                      {e.response_level && (
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500 border border-white/10 px-2.5 py-1">
                          {e.response_level}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">
                      {e.email} {e.phone && `· ${e.phone}`} {e.company && `· ${e.company}`}
                    </p>
                    <p className="font-mono text-xs uppercase tracking-[0.15em] text-gray-500 mt-2">
                      {e.facility_type} · Threat: {e.threat_level}
                    </p>
                    {e.services?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {e.services.map((s) => (
                          <span key={s} className="font-mono text-[10px] uppercase tracking-[0.1em] bg-white/5 border border-white/10 text-gray-400 px-2 py-1">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    {e.message && <p className="text-sm text-gray-500 mt-3 max-w-2xl leading-relaxed">"{e.message}"</p>}
                    <p className="font-mono text-[10px] text-gray-600 mt-3">
                      {new Date(e.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      data-testid={`status-select-${e.id}`}
                      value={e.status}
                      onChange={(ev) => updateStatus(e.id, ev.target.value)}
                      className="bg-[#08090B] border border-white/10 focus:border-blue-500/60 outline-none px-3 py-2 text-xs font-mono uppercase tracking-[0.1em] text-gray-300"
                    >
                      {Object.entries(STATUS_META).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    <button
                      data-testid={`delete-enquiry-${e.id}`}
                      onClick={() => remove(e.id)}
                      className="clip-corners border border-white/15 p-2 text-gray-500 hover:text-blue-400 hover:border-blue-500/50 transition-colors duration-300"
                      aria-label="Delete enquiry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
